import React from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Checkbox,
  Separator,
  Button,
} from '@roger/ui-components';
import { ChevronDown, Check } from 'lucide-react';
import {
  type EnabledTabs,
  DEFAULT_ENABLED_TABS,
} from '../constants/intakeTabs';

export type StatusFilterType =
  | 'show_all'
  | 'completed'
  | 'non_admit'
  | 'incomplete_extraction'
  | 'incomplete_eligibility'
  | 'incomplete_prior_auth'
  | 'incomplete_ready_to_call'
  | 'incomplete_scheduling'
  | 'incomplete_push_to_emr';

interface IntakeStatusFilterProps {
  value: StatusFilterType[];
  onChange: (value: StatusFilterType[]) => void;
  enabledTabs?: EnabledTabs;
}

const INCOMPLETE_OPTIONS: {
  key: StatusFilterType;
  label: string;
  tabKey: string;
}[] = [
  { key: 'incomplete_extraction', label: 'Extraction', tabKey: 'extraction' },
  {
    key: 'incomplete_eligibility',
    label: 'Eligibility',
    tabKey: 'eligibility',
  },
  { key: 'incomplete_prior_auth', label: 'Prior Auth', tabKey: 'prior_auth' },
  {
    key: 'incomplete_ready_to_call',
    label: 'Ready to Call',
    tabKey: 'ready_to_call',
  },
  { key: 'incomplete_scheduling', label: 'Scheduling', tabKey: 'scheduling' },
  {
    key: 'incomplete_push_to_emr',
    label: 'Push to EMR',
    tabKey: 'push_to_emr',
  },
];

const IntakeStatusFilter: React.FC<IntakeStatusFilterProps> = ({
  value,
  onChange,
  enabledTabs = DEFAULT_ENABLED_TABS,
}) => {
  const [open, setOpen] = React.useState(false);

  // Filter incomplete options based on enabled tabs
  const visibleIncompleteOptions = INCOMPLETE_OPTIONS.filter(opt => {
    if (opt.tabKey === 'extraction') return true; // Always show extraction
    return enabledTabs[opt.tabKey as keyof typeof enabledTabs] !== false;
  });

  const isShowAll = value.length === 0 || value.includes('show_all');
  const isCompleted = value.includes('completed');
  const isNonAdmit = value.includes('non_admit');
  const incompleteFilters = value.filter(v => v.startsWith('incomplete_'));

  const handleShowAll = () => {
    onChange([]);
    setOpen(false);
  };

  const handleCompleted = () => {
    onChange(['completed']);
    setOpen(false);
  };

  const handleNonAdmit = () => {
    onChange(['non_admit']);
    setOpen(false);
  };

  const handleIncompleteToggle = (key: StatusFilterType) => {
    // If currently in a mutually exclusive state, switch to incomplete mode
    if (isShowAll || isCompleted || isNonAdmit) {
      onChange([key]);
      return;
    }

    // Toggle the incomplete filter
    if (incompleteFilters.includes(key)) {
      const newFilters = incompleteFilters.filter(f => f !== key);
      onChange(
        newFilters.length === 0 ? [] : (newFilters as StatusFilterType[])
      );
    } else {
      onChange([...incompleteFilters, key] as StatusFilterType[]);
    }
  };

  // Get display text for the button
  const getDisplayText = () => {
    if (isShowAll) return 'All Statuses';
    if (isCompleted) return 'Completed';
    if (isNonAdmit) return 'Non-Admit';
    if (incompleteFilters.length === 1) {
      const opt = INCOMPLETE_OPTIONS.find(o => o.key === incompleteFilters[0]);
      return opt ? `Incomplete: ${opt.label}` : 'Filtered';
    }
    if (incompleteFilters.length > 1) {
      return `${incompleteFilters.length} Incomplete Steps`;
    }
    return 'All Statuses';
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          className='h-9 justify-between min-w-[180px] font-normal'
        >
          <span className='truncate'>{getDisplayText()}</span>
          <ChevronDown className='h-4 w-4 opacity-50 ml-2 flex-shrink-0' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-56 p-0' align='start'>
        <div className='p-1'>
          {/* Show All */}
          <button
            onClick={handleShowAll}
            className={`
              w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm
              hover:bg-muted transition-colors text-left
              ${isShowAll ? 'text-foreground font-medium' : 'text-muted-foreground'}
            `}
          >
            {isShowAll && <Check className='h-4 w-4 text-primary' />}
            {!isShowAll && <div className='w-4' />}
            Show All
          </button>

          <Separator className='my-1' />

          {/* Completed */}
          <button
            onClick={handleCompleted}
            className={`
              w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm
              hover:bg-muted transition-colors text-left
              ${isCompleted ? 'text-foreground font-medium' : 'text-muted-foreground'}
            `}
          >
            {isCompleted && <Check className='h-4 w-4 text-primary' />}
            {!isCompleted && <div className='w-4' />}
            Completed
          </button>

          <Separator className='my-1' />

          {/* Non-Admit */}
          <button
            onClick={handleNonAdmit}
            className={`
              w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm
              hover:bg-muted transition-colors text-left
              ${isNonAdmit ? 'text-foreground font-medium' : 'text-muted-foreground'}
            `}
          >
            {isNonAdmit && <Check className='h-4 w-4 text-primary' />}
            {!isNonAdmit && <div className='w-4' />}
            Non-Admit
          </button>

          <Separator className='my-1' />

          {/* Incomplete Steps (multi-select) */}
          <div className='py-1'>
            <div className='px-3 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider'>
              Incomplete Steps
            </div>
            {visibleIncompleteOptions.map(opt => {
              const isChecked = incompleteFilters.includes(opt.key);
              return (
                <button
                  key={opt.key}
                  onClick={() => handleIncompleteToggle(opt.key)}
                  className={`
                    w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm
                    hover:bg-muted transition-colors text-left
                    ${isChecked ? 'text-foreground' : 'text-muted-foreground'}
                  `}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => handleIncompleteToggle(opt.key)}
                    className='pointer-events-none'
                  />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default IntakeStatusFilter;
